import { useState, useEffect } from 'react'

/**
 * Custom hook for managing smooth page transitions
 * 
 * This hook helps prevent UI freezing when navigating between routes with 
 * heavy components by deferring the rendering of complex content.
 * 
 * It returns a boolean indicating whether the component is in loading state,
 * which can be used to render placeholder content initially.
 * 
 * @param {number} initialDelay - Optional delay in ms before rendering actual content (default: 0)
 * @param {boolean} initialState - Optional initial loading state (default: true)
 * @returns {boolean} - True if page is in transition/loading, false when ready to display content
 * 
 * @example
 * // Basic usage
 * const isLoading = usePageTransition();
 * return isLoading ? <Skeleton /> : <ActualContent />;
 * 
 * // With custom delay
 * const isLoading = usePageTransition(100);
 * 
 * // With custom initial state (start already loaded)
 * const isLoading = usePageTransition(0, false);
 */
const usePageTransition = (initialDelay = 0, initialState = true) => {
    const [isLoading, setIsLoading] = useState(initialState)
    
    useEffect(() => {
        if (!initialState) return // Skip transition if starting loaded
        
        // Use requestAnimationFrame to ensure the browser has completed rendering
        // the initial frame before scheduling our state update
        const animationFrame = requestAnimationFrame(() => {
            const timer = setTimeout(() => {
                setIsLoading(false)
            }, initialDelay)
            
            return () => clearTimeout(timer)
        })
        
        return () => {
            cancelAnimationFrame(animationFrame)
        }
    }, [initialDelay, initialState])
    
    return isLoading
}

export default usePageTransition 